import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import MatchCard from "../../components/cards/match.card";
import "@xyflow/react/dist/style.css";
import { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from "@mui/material";
import { setWinner } from "../../api/tournament.api";
import { useAuth } from "../../context/auth.context";

const RoundRobinMatchesComponent = ({ matches, participants, refresh }) => {
  const location = useLocation();
  const auth = useAuth();
  const tournament = useMemo(
    () => location.state.tournament || {},
    [location.state.tournament],
  );

  const rows = useMemo(() => {
    if (!matches.length) return [];

    const maxLevel = getMaxLevel(matches);

    return Array.from({ length: maxLevel + 1 }, (_, index) => ({
      index,
      matches: getMatchesOfLevel(matches, index),
    }));
  }, [matches]);
  
  const getMaxLevel = (matches) => {
    return Math.max(...matches.map((item) => item.level));
  };

  const getMatchesOfLevel = (matches, level) => {
    return matches.filter((m) => m.level === level);
  };

  const getTotalPointsAndWins = useCallback(
    (participantId) => {
      let wins = 0;
      let totalPoints = 0;
      matches.forEach((match) => {
        match.participants.forEach((participant) => {
          if (participant.id === participantId) {
            if (participant.isWinner) {
              wins++;
            }
            if (participant.points) {
              totalPoints += participant.points;
            }
          }
        });
      });

      return { wins, totalPoints };
    },
    [matches],
  );

  const compareParticipants = (a, b) => {
    var valA = getTotalPointsAndWins(a.id);
    var valB = getTotalPointsAndWins(b.id);
    if (valA.wins < valB.wins) {
      return 1;
    }
    if (valA.wins > valB.wins) {
      return -1;
    }
    if (valA.totalPoints < valB.totalPoints) {
      return 1;
    }
    if (valA.totalPoints > valB.totalPoints) {
      return -1;
    }
    return 0;
  };

  const areAllMatchesClosedAndIsTournamentRunning = () => {
    let unfinishedMatches =
      matches.filter((m) => m.status !== "Closed").length > 0;
    return tournament.status === "Started" && !unfinishedMatches;
  };

  const setWinnerAndCloseTournament = (participantId) => {
    setWinner(tournament.id, participantId)
      .then((response) => {
        refresh();
      })
      .catch((error) => {});
  };

  useEffect(() => {
    var rows = [];
    if (matches.length > 0) {
      let maxLevel = getMaxLevel(matches);
      for (let i = 0; i <= maxLevel; i++) {
        rows.push({ index: i, matches: getMatchesOfLevel(matches, i) });
      }
      setRows(rows);
    }
  }, [matches, participants]);

  return (
    <Box>
      <TableContainer>
        <Table aria-label="match table">
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.index}>
                <TableCell>Round: {row.index + 1}</TableCell>
                <TableCell align="right">
                  <Grid container spacing={1}>
                    {row.matches.map((match) => (
                      <MatchCard
                        key={match.id}
                        data={{
                          match: match,
                          tournamentId: tournament.id,
                          refresh: refresh,
                        }}
                      />
                    ))}
                  </Grid>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="h5">Result</Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Participant</TableCell>
              <TableCell>Total Points</TableCell>
              <TableCell>Wins</TableCell>
              {auth &&
                auth.user &&
                auth.user.role === "Admin" &&
                tournament.system === "RoundRobin" && (
                  <TableCell>Actions</TableCell>
                )}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...participants].sort(compareParticipants).map((row, index) => (
              <TableRow
                key={row.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>{index + 1}.</TableCell>
                <TableCell>
                  {tournament.participantMode === "Single"
                    ? row.nickname
                    : row.name}
                </TableCell>
                <TableCell>
                  {getTotalPointsAndWins(row.id).totalPoints}
                </TableCell>
                <TableCell>{getTotalPointsAndWins(row.id).wins} </TableCell>
                {auth &&
                  auth.user &&
                  auth.user.role === "Admin" &&
                  tournament.system === "RoundRobin" && (
                    <TableCell>
                      {index === 0 &&
                        areAllMatchesClosedAndIsTournamentRunning() && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setWinnerAndCloseTournament(row.id)}
                          >
                            Set Winner
                          </Button>
                        )}
                    </TableCell>
                  )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RoundRobinMatchesComponent;
